// App instalable y notificaciones push del navegador.
// - Instalar: Android y computadoras ofrecen «Instalar» (beforeinstallprompt); en iPhone se hace
//   desde Compartir → «Agregar a inicio». En iPhone, las notificaciones solo funcionan con la app instalada.
// - Push: cada dispositivo se suscribe con la llave pública del servidor y queda en la cuenta.
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get, post } from './api';
import { t } from './i18n';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Se llama una vez al cargar la app. */
export function initPwa() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* sin service worker: la app funciona igual, sin push */ });
    // Al tocar una notificación con la app abierta en una ventana que el service worker no controla.
    navigator.serviceWorker.addEventListener('message', (e) => {
      const d = e.data as { type?: string; url?: string } | null;
      if (d?.type === 'navigate' && typeof d.url === 'string' && new URL(d.url, location.origin).origin === location.origin) location.assign(d.url);
    });
  }
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredPrompt = e as BeforeInstallPromptEvent; emit(); });
  window.addEventListener('appinstalled', () => { deferredPrompt = null; installed = true; emit(); });
}

export function isStandalone(): boolean {
  return installed || window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export interface InstallState { standalone: boolean; canPrompt: boolean; ios: boolean }
let snapshot: InstallState | null = null;
function installSnapshot(): InstallState {
  const next = { standalone: isStandalone(), canPrompt: !!deferredPrompt, ios: isIos() };
  if (!snapshot || snapshot.standalone !== next.standalone || snapshot.canPrompt !== next.canPrompt || snapshot.ios !== next.ios) snapshot = next;
  return snapshot;
}

export function useInstall() {
  const state = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, installSnapshot);
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    const p = deferredPrompt;
    deferredPrompt = null;
    await p.prompt();
    const { outcome } = await p.userChoice;
    emit();
    return outcome === 'accepted';
  }, []);
  return { ...state, promptInstall };
}

// ---------- Push ----------

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), 5000))]);
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

function keyBytes(base64url: string): Uint8Array {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array) => !!a && a.byteLength === b.byteLength && new Uint8Array(a).every((v, i) => v === b[i]);

/** Pide permiso y registra este dispositivo. Devuelve false si la persona no dio permiso. */
export async function enablePush(publicKey: string): Promise<boolean> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const reg = await registration();
  if (!reg) throw new Error(t('No se pudieron activar las notificaciones en este dispositivo.'));
  const key = keyBytes(publicKey);
  let sub = await reg.pushManager.getSubscription();
  // Las llaves del servidor cambiaron: la suscripción anterior ya no sirve.
  if (sub && !sameKey(sub.options.applicationServerKey, key)) { await sub.unsubscribe(); sub = null; }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await post('/api/me/push-subscriptions', sub.toJSON());
  return true;
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await post('/api/me/push-subscriptions/remove', { endpoint: sub.endpoint }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/** Al cerrar sesión: este dispositivo deja de recibir los avisos de esta cuenta. */
export async function forgetPushOnLogout(): Promise<void> {
  try { await disablePush(); } catch { /* sin push */ }
}

/**
 * Al abrir la app con sesión: si este dispositivo ya tenía las notificaciones, se vuelve a
 * registrar (el servidor las quita al cambiar la contraseña o si el navegador cambió las llaves).
 */
export async function syncPush(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const sub = await currentSubscription();
    if (sub) await post('/api/me/push-subscriptions', sub.toJSON());
  } catch { /* se intentará la próxima vez */ }
}

export type PushState = 'loading' | 'unsupported' | 'server-off' | 'needs-install' | 'denied' | 'off' | 'on';

/** Estado de las notificaciones en este dispositivo y acciones para activarlas o desactivarlas. */
export function usePush() {
  const config = useQuery({ queryKey: ['push-config'], queryFn: () => get<{ enabled: boolean; publicKey: string | null }>('/api/push/config'), staleTime: 300_000 });
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    const sub = await currentSubscription().catch(() => null);
    setSubscribed(!!sub && Notification.permission === 'granted');
    setEndpoint(sub?.endpoint ?? null);
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  let state: PushState;
  if (!pushSupported()) state = isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  else if (!config.data || subscribed === null) state = 'loading';
  else if (!config.data.enabled) state = 'server-off';
  else if (Notification.permission === 'denied') state = 'denied';
  else state = subscribed ? 'on' : 'off';
  const enable = useCallback(async () => {
    const ok = config.data?.publicKey ? await enablePush(config.data.publicKey) : false;
    await refresh();
    return ok;
  }, [config.data, refresh]);
  const disable = useCallback(async () => { await disablePush(); await refresh(); }, [refresh]);
  return { state, endpoint, enable, disable, refresh };
}
